import React, { useEffect, useState, useMemo, useContext } from "react";
import Select, { components } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useProjectContributors } from "../queries/use-project-contributors";
import { useLanguage } from "../context/LanguageContext";
import { useUpdateProjectContributors } from "../queries/use-update-project-contributors";
import { useDeleteProjectContributors } from "../queries/use-delete-project-contributors";
import { useAddProjectContributors } from "../queries/use-add-project-contributors";

const ValueContainer = ({ children, getValue, ...props }) => {
  const values = getValue();
  let valueLabel = "";

  if (values.length > 0)
    valueLabel += props.selectProps.getOptionLabel(values[0]);
  if (values.length > 1) valueLabel += ` & ${values.length - 1} more`;

  // Keep standard placeholder and input from react-select
  const childsToRender = React.Children.toArray(children).filter(
    (child) =>
      ["Input", "DummyInput", "Placeholder"].indexOf(child.type.name) >= 0
  );

  return (
    <components.ValueContainer {...props}>
      {!props.selectProps.inputValue && valueLabel}
      {childsToRender}
    </components.ValueContainer>
  );
};

const Contributor = ({ projectId, contributor }) => {
  const { languages } = useLanguage();
  const languageIds = contributor.languages.map((l) => l.languageId);
  const [selected, setSelected] = useState([]);
  const [role, setRole] = useState(contributor.role);
  useEffect(() => {
    const filtered = languages.filter((l) => languageIds.includes(l.id));
    setSelected(
      filtered.map((f) => ({
        label: f.name,
        value: f.id,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages, languageIds.join(",")]);
  const { mutateAsync, isLoading } = useUpdateProjectContributors(
    projectId,
    contributor.contributorId
  );

  const { mutateAsync: deleteContributor } = useDeleteProjectContributors(
    projectId,
    contributor.contributorId
  );

  const handleChange = async (selected) => {
    setSelected(selected);
    await mutateAsync({
      languages: selected.map((s) => s.value),
      role,
    });
  };

  const handleRoleChange = async (role) => {
    setRole(role);
    await mutateAsync({
      languages: selected.map((s) => s.value),
      role,
    });
  };

  return (
    <div
      className="block text-sm font-medium text-gray-700 flex flex-row items-center mb-2"
      key={contributor.id}
    >
      <div className="flex-1">{contributor.contributor.email}</div>
      <select
        value={role}
        onChange={(e) => handleRoleChange(e.target.value)}
        className="mx-2"
      >
        <option value="REVIEWER">Reviewer</option>
        <option value="TRANSLATOR">Translator</option>
      </select>
      <Select
        className="w-1/2 border-gray-300"
        closeMenuOnSelect={false}
        isMulti
        onChange={handleChange}
        options={languages.map((l) => ({
          label: l.name,
          value: l.id,
        }))}
        value={selected}
        components={{
          ValueContainer,
        }}
        hideSelectedOptions={false}
      />
      <button
        className="ml-2 inline-flex items-center justify-center text-gray-400 h-8 w-8"
        onClick={() => deleteContributor()}
      >
        <FontAwesomeIcon className="w-4 h-4" icon={faTimes} />
      </button>
    </div>
  );
};

const ContributorForm = ({ projectId }) => {
  const { languages } = useLanguage();
  const [selected, setSelected] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("TRANSLATOR");
  const { mutateAsync, error } = useAddProjectContributors(projectId);

  const handleChange = async (selected) => {
    setSelected(selected);
  };

  const submit = async (e) => {
    e.preventDefault();
    await mutateAsync({
      email,
      languages: selected.map((s) => s.value),
      role,
    });
    setEmail("");
    setSelected([]);
  };

  return (
    <div>
      <hr className="mt-8 mb-4" />
      <h4>Add Contributor</h4>
      <form
        onSubmit={submit}
        className="block text-sm font-medium text-gray-700 flex flex-row items-center mb-2"
      >
        <input
          className="flex-1 h-8 px-2 border-b border-gray-700 mr-2"
          value={email}
          placeholder="email"
          onChange={(e) => setEmail(e.target.value)}
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="mx-2"
        >
          <option value="REVIEWER">Reviewer</option>
          <option value="TRANSLATOR">Translator</option>
        </select>
        <Select
          className="w-1/2 border-gray-300"
          closeMenuOnSelect={false}
          isMulti
          onChange={handleChange}
          options={languages.map((l) => ({
            label: l.name,
            value: l.id,
          }))}
          value={selected}
          components={{
            ValueContainer,
          }}
          menuPlacement="top"
          hideSelectedOptions={false}
        />
        <button
          className="ml-2 inline-flex items-center justify-center h-8 w-8"
          onClick={null}
        >
          Add
        </button>
      </form>
      {error && <div className="text-red-500">No such email</div>}
    </div>
  );
};

const EditProjectContributorsPopup = ({ projectId, onClose }) => {
  const { data: contributors } = useProjectContributors(projectId);

  if (!contributors) {
    return <div />;
  }

  return (
    <div
      className="fixed z-10 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center h-screen text-center p-10">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="flex flex-col bg-white rounded-lg text-left shadow-xl transform transition-all items-stretch w-full h-full overflow-hidden">
          <div className="flex-1 flex flex-col px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex-1 flex flex-col mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3
                className="text-lg leading-6 font-medium text-gray-900"
                id="modal-title"
              >
                Contributors
              </h3>
              <div className="flex-1 mt-2 text-sm text-gray-500 overflow-scroll">
                {contributors.map((contributor) => (
                  <Contributor
                    projectId={projectId}
                    contributor={contributor}
                    key={contributor.id}
                  />
                ))}
              </div>

              <div>
                <ContributorForm projectId={projectId} />
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => onClose()}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProjectContributorsPopup;
